import type { ComboboxItem } from "@mantine/core";
import { Alert, Group, Paper, Select, Tabs } from "@mantine/core";
import type { DatesRangeValue } from "@mantine/dates";
import { MonthPickerInput } from "@mantine/dates";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Userpilot } from "userpilot";
import { useOperators } from "../../hooks/useOperators";
import { AlertPresets } from "../../mantine/mantinePresets";
import { OperatorFeatures } from "../../urls";
import { backendDateOrDateTimeToLuxonDateTime } from "../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { PageContent } from "../BuildingBlocks/Layout/PageContent/PageContent";
import { PageHeader } from "../BuildingBlocks/Layout/PageHeader/PageHeader";
import { IconHelpText } from "../IconHelpText/IconHelpText";
import { LoadOrError } from "../LoadOrError/LoadOrError";
import { CompanySection } from "./EnergyRevenuesPage/Sections/CompanySection";
import { GeneratorSection } from "./EnergyRevenuesPage/Sections/GeneratorSection";
import { getMinOrMaxDateFromEnergyRevenues } from "./EnergyRevenuesPage/utils/getMinOrMaxDateFromEnergyRevenues";
import { useEnergyRevenuesPageParams } from "./hooks/useEnergyRevenuesPageParams";
import { usePersonEnergyRevenuesData } from "./hooks/usePersonEnergyRevenuesData";
import { usePersonEnergyRevenuesVerificationItems } from "./hooks/usePersonEnergyRevenuesVerificationItems";
import type { RevenueTabs } from "./RevenueMonitoring.types";

function RevenueMonitoringView() {
  const { t } = useTranslation();
  const { tab, setTab, companyId, setCompanyId } =
    useEnergyRevenuesPageParams();
  const {
    data: companies,
    isLoading: isLoadingCompanies,
    error: companiesError
  } = useOperators({ activeFeatures: [OperatorFeatures.RevenueMonitoring] });

  const companyOptions =
    companies?.map<ComboboxItem>((company) => ({
      label: company.name,
      value: company.id.toString()
    })) ?? null;

  const selectedCompany = companyOptions?.find(
    (company) => company.value === String(companyId)
  );

  useEffect(() => {
    // always make sure the companyId is valid
    if (!companyOptions) {
      return;
    }
    const firstCompanyValue = companyOptions[0]?.value;
    if (
      !companyOptions.find((company) => company.value === String(companyId))
    ) {
      setCompanyId(Number(firstCompanyValue));
    } else if (companyId) {
      // if companyId is valid but not in URL, sync it to URL
      setCompanyId(companyId);
    }
  }, [companyOptions, companyId, setCompanyId]);

  const handleSelectedCompanyId = (newCompanyId) => {
    setCompanyId(newCompanyId);
    Userpilot.track("em_unternehmen_feld");
  };

  const {
    data: energyRevenuesData,
    isLoading: isEnergyRevenuesDataLoading,
    error: energyRevenuesDataError
  } = usePersonEnergyRevenuesData(companyId);

  const { data: verificationItems, error: verificationItemsError } =
    usePersonEnergyRevenuesVerificationItems(companyId);

  const [selectedDateRange, setSelectedDateRange] = useState<
    [DateTime<true>, DateTime<true> | null]
  >([
    DateTime.now().startOf("year") as DateTime<true>,
    DateTime.now().endOf("month") as DateTime<true>
  ]);

  useEffect(() => {
    const maxDate = getMinOrMaxDateFromEnergyRevenues(
      energyRevenuesData ?? [],
      "max"
    )?.endOf("month");

    const datesInYearOfMaxDate = energyRevenuesData?.filter((item) => {
      const revenuesDate = backendDateOrDateTimeToLuxonDateTime(
        item.period_start
      );
      return revenuesDate.year === maxDate?.year;
    });

    const minDate = getMinOrMaxDateFromEnergyRevenues(
      datesInYearOfMaxDate ?? [],
      "min"
    )?.startOf("month");

    const validMaxDate = maxDate?.isValid
      ? (maxDate as DateTime<true>)
      : DateTime.now().endOf("month");
    const validMinDate = minDate?.isValid
      ? (minDate as DateTime<true>)
      : validMaxDate.startOf("year");

    setSelectedDateRange([validMinDate, validMaxDate]);
  }, [energyRevenuesData]);

  const selectedEnergyRevenuesData = useMemo(() => {
    return energyRevenuesData?.filter((energyRevenues) => {
      const revenuesDate = backendDateOrDateTimeToLuxonDateTime(
        energyRevenues.period_start
      );

      return (
        selectedDateRange[0] <= revenuesDate &&
        (selectedDateRange[1] === null || revenuesDate <= selectedDateRange[1])
      );
    });
  }, [energyRevenuesData, selectedDateRange]);

  const verificationItemsFilteredByDate = useMemo(() => {
    return verificationItems?.filter((verificationItem) => {
      const verificationItemDate = backendDateOrDateTimeToLuxonDateTime(
        verificationItem.period_start
      );

      return (
        selectedDateRange[0] <= verificationItemDate &&
        (selectedDateRange[1] === null ||
          verificationItemDate <= selectedDateRange[1])
      );
    });
  }, [selectedDateRange, verificationItems]);

  function handleDatePickerChange(dateRange: DatesRangeValue<string>) {
    // only react if a valid & complete range is set
    if (dateRange[0] && (dateRange[1] || dateRange[1] === null)) {
      const firstDate = DateTime.fromISO(dateRange[0]);
      const lastDate = dateRange[1]
        ? DateTime.fromISO(dateRange[1]).endOf("month")
        : null;
      if (firstDate.isValid && (lastDate === null || lastDate.isValid)) {
        setSelectedDateRange([
          firstDate as DateTime<true>,
          lastDate ? (lastDate as DateTime<true>) : null
        ]);
      }
    }
  }

  function handleTabChange(newTab: string | null) {
    if (!newTab) {
      return;
    }
    setTab(newTab as RevenueTabs);
  }

  if (companiesError) {
    return <LoadOrError error={companiesError} />;
  }

  return (
    <PageContent className="RevenueMonitoringView">
      <PageHeader title="Erlösmonitoring" />
      <LoadOrError error={companiesError}>
        <Group gap="xl" mb="lg">
          <Select
            className="person-select"
            data={companyOptions ?? []}
            disabled={
              isLoadingCompanies || (companies && companies.length === 0)
            }
            label="Unternehmen"
            placeholder="Unternehmen auswählen"
            size="xs"
            value={selectedCompany?.value}
            w={624}
            onChange={handleSelectedCompanyId}
          />

          <MonthPickerInput
            allowSingleDateInRange
            aria-label="Berechneter Zeitraum"
            disabled={
              isEnergyRevenuesDataLoading || energyRevenuesData?.length === 0
            }
            label={
              <Group align="center" gap="xs">
                {isEnergyRevenuesDataLoading
                  ? "Lade Zeiträume..."
                  : "Berechneter Zeitraum"}
                <IconHelpText
                  helpText={
                    "Bitte beachten Sie, dass wir Ihnen nur Zeiträume zur Auswahl stellen können, für die bereits eine Berechnung vorliegt. Fehlen beispielsweise Energiedaten oder Marktwerte, steht der entsprechende Zeitraum leider nicht zur Verfügung."
                  }
                  size={16}
                />
              </Group>
            }
            maxDate={getMinOrMaxDateFromEnergyRevenues(
              energyRevenuesData ?? [],
              "max"
            )?.toJSDate()}
            minDate={getMinOrMaxDateFromEnergyRevenues(
              energyRevenuesData ?? [],
              "min"
            )?.toJSDate()}
            size="xs"
            type="range"
            value={[
              selectedDateRange[0].toJSDate(),
              selectedDateRange[1]?.toJSDate() || null
            ]}
            onChange={handleDatePickerChange}
          />
        </Group>
      </LoadOrError>
      <Paper>
        <LoadOrError error={energyRevenuesDataError}>
          {isLoadingCompanies || (companies && companies.length > 0) ? (
            <Tabs
              defaultValue={tab ?? "unternehmen"}
              onChange={handleTabChange}
            >
              <Tabs.List mb="lg">
                <Tabs.Tab value="unternehmen">Unternehmensübersicht</Tabs.Tab>
                <Tabs.Tab value="anlagen">Anlagenübersicht</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="unternehmen">
                <LoadOrError error={verificationItemsError}>
                  <CompanySection
                    companyId={companyId}
                    dataVerificationItems={verificationItemsFilteredByDate}
                    energyRevenuesData={selectedEnergyRevenuesData ?? []}
                    exportFileName={selectedCompany?.label || ""}
                    isLoading={
                      !selectedEnergyRevenuesData ||
                      !verificationItemsFilteredByDate
                    }
                    selectedDateRange={selectedDateRange}
                  />
                </LoadOrError>
              </Tabs.Panel>
              <Tabs.Panel value="anlagen">
                {selectedCompany && (
                  <GeneratorSection
                    companyId={Number(companyId)}
                    companyLabel={selectedCompany?.label}
                    isLoading={!selectedEnergyRevenuesData}
                    selectedDateRange={selectedDateRange}
                  />
                )}
              </Tabs.Panel>
            </Tabs>
          ) : (
            <Alert
              color={AlertPresets.error.color}
              icon={AlertPresets.error.icon}
            >
              {t("errors.RevenueMonitoring.NoCompaniesError")}
            </Alert>
          )}
        </LoadOrError>
      </Paper>
    </PageContent>
  );
}

export { RevenueMonitoringView };
